"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@/components/auth/Auth";
import { publicEnv } from "@/config/env";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import { useWaveTopVoters } from "@/hooks/useWaveTopVoters";
import { getAuthJwt, getStagingAuth } from "@/services/auth/auth.utils";
import { sanitizeErrorForUser } from "@/utils/error-sanitizer";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { AnimatePresence, motion } from "framer-motion";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import useDownloader from "@/hooks/useDownloader";
import CircleLoader, { CircleLoaderSize } from "@/components/distribution-plan-tool/common/CircleLoader";
import { SingleWaveDropVoter } from "./SingleWaveDropVoter";
interface SingleWaveDropVotersProps {
  readonly drop: ApiDrop;
}
const getSafeCsvFilenameId = (dropId: string): string => {
  const safeId = dropId.replaceAll(/[/\\:*?"<>|]/g, "_").replaceAll(/\s+/g, "_").slice(0, 180);
  return safeId || "drop";
};
export const SingleWaveDropVoters: React.FC<SingleWaveDropVotersProps> = t0 => {
  const $ = _c(61);
  const {
    drop
  } = t0;
  const {
    connectedProfile,
    setToast
  } = useAuth();
  const [isVotersOpen, setIsVotersOpen] = useState(false);
  const {
    download,
    error: downloadError,
    isInProgress
  } = useDownloader();
  const t1 = connectedProfile?.handle ?? undefined;
  let t2;
  if ($[0] !== drop.id || $[1] !== drop.wave.id || $[2] !== isVotersOpen || $[3] !== t1) {
    t2 = {
      waveId: drop.wave.id,
      connectedProfileHandle: t1,
      reverse: false,
      dropId: drop.id,
      sortDirection: "DESC",
      sort: "ABSOLUTE",
      enabled: isVotersOpen
    };
    $[0] = drop.id;
    $[1] = drop.wave.id;
    $[2] = isVotersOpen;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    voters,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isLoading
  } = useWaveTopVoters(t2);
  const downloadUrl = `${publicEnv.API_ENDPOINT}/api/v2/drops/${encodeURIComponent(drop.id)}/votes/download`;
  const buildDownloadHeaders = _temp;
  let t3;
  let t4;
  if ($[5] !== downloadError || $[6] !== setToast) {
    t3 = () => {
      if (!downloadError?.errorMessage) {
        return;
      }
      setToast({
        type: "error",
        message: `Download failed: ${sanitizeErrorForUser(downloadError.errorMessage)}`
      });
    };
    t4 = [downloadError, setToast];
    $[5] = downloadError;
    $[6] = setToast;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  if ($[9] !== download || $[10] !== downloadUrl || $[11] !== drop.id || $[12] !== isInProgress) {
    t5 = async () => {
      if (isInProgress) {
        return;
      }
      const safeId = getSafeCsvFilenameId(drop.id);
      await download(downloadUrl, `drop-votes-${safeId}.csv`, undefined, {
        headers: buildDownloadHeaders()
      });
    };
    $[9] = download;
    $[10] = downloadUrl;
    $[11] = drop.id;
    $[12] = isInProgress;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const onDownloadAllVotes = t5;
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => setIsVotersOpen(_temp2);
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const toggleVoters = t6;
  let t7;
  if ($[15] !== fetchNextPage || $[16] !== hasNextPage || $[17] !== isFetchingNextPage || $[18] !== isLoading) {
    t7 = () => {
      if (hasNextPage && !isLoading && !isFetchingNextPage) {
        fetchNextPage();
      }
    };
    $[15] = fetchNextPage;
    $[16] = hasNextPage;
    $[17] = isFetchingNextPage;
    $[18] = isLoading;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const intersectionElementRef = useIntersectionObserver(t7);
  const t8 = `tw-flex tw-w-full tw-items-center tw-justify-between tw-border-0 tw-px-4 tw-py-4 tw-text-left tw-transition-colors tw-duration-300 tw-ease-out desktop-hover:hover:tw-bg-iron-900 ${isVotersOpen ? "tw-bg-iron-800" : "tw-bg-iron-950"}`;
  const t9 = `tw-text-sm tw-font-medium ${isVotersOpen ? "tw-text-iron-300" : "tw-text-iron-400"}`;
  let t10;
  if ($[20] !== t9) {
    t10 = <button type="button" onClick={toggleVoters} className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-border-0 tw-bg-transparent tw-p-0 tw-text-left"><span className={t9}>Top voters</span></button>;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const t11 = isInProgress ? "Downloading" : "Download All";
  let t12;
  if ($[22] !== t11) {
    t12 = <span className="tw-w-[5.5rem] tw-whitespace-nowrap tw-text-left">{t11}</span>;
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== isInProgress) {
    t13 = <span className="tw-flex tw-size-4 tw-flex-shrink-0 tw-items-center tw-justify-center">{isInProgress ? <CircleLoader size={CircleLoaderSize.SMALL} /> : <ArrowDownTrayIcon className="tw-size-4" />}</span>;
    $[24] = isInProgress;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== isInProgress || $[27] !== onDownloadAllVotes || $[28] !== t12 || $[29] !== t13) {
    t14 = <button type="button" onClick={onDownloadAllVotes} disabled={isInProgress} className="tw-flex tw-h-8 tw-w-[9.25rem] tw-items-center tw-justify-center tw-gap-x-2 tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-px-3 tw-text-xs tw-font-medium tw-text-iron-300 tw-transition-colors tw-duration-300 tw-ease-out hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white disabled:tw-cursor-not-allowed disabled:tw-opacity-60" aria-label="Download all top voters as CSV">{t12}{t13}</button>;
    $[26] = isInProgress;
    $[27] = onDownloadAllVotes;
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const t15 = `${isVotersOpen ? "Collapse" : "Expand"} top voters`;
  const t16 = isVotersOpen ? 180 : 0;
  let t17;
  if ($[31] !== t16) {
    t17 = {
      rotate: t16
    };
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      duration: 0.3
    };
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  const t19 = `tw-h-4 tw-w-4 tw-flex-shrink-0 ${isVotersOpen ? "tw-text-iron-400" : "tw-text-iron-600"}`;
  let t20;
  if ($[34] !== t19) {
    t20 = <ChevronDownIcon className={t19} />;
    $[34] = t19;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== t17 || $[37] !== t20) {
    t21 = <motion.div animate={t17} transition={t18}>{t20}</motion.div>;
    $[36] = t17;
    $[37] = t20;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] !== t15 || $[40] !== t21) {
    t22 = <button type="button" onClick={toggleVoters} className="tw-flex tw-size-6 tw-items-center tw-justify-center tw-border-0 tw-bg-transparent tw-p-0" aria-label={t15}>{t21}</button>;
    $[39] = t15;
    $[40] = t21;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== t14 || $[43] !== t22) {
    t23 = <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-x-3">{t14}{t22}</div>;
    $[42] = t14;
    $[43] = t22;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  let t24;
  if ($[45] !== t10 || $[46] !== t23 || $[47] !== t8) {
    t24 = <div className={t8}>{t10}{t23}</div>;
    $[45] = t10;
    $[46] = t23;
    $[47] = t8;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] !== drop.wave.voting_credit_type || $[50] !== intersectionElementRef || $[51] !== isFetchingNextPage || $[52] !== isLoading || $[53] !== isVotersOpen || $[54] !== voters) {
    t25 = isVotersOpen && <motion.div initial={{
      height: 0,
      opacity: 0
    }} animate={{
      height: "auto",
      opacity: 1
    }} exit={{
      height: 0,
      opacity: 0
    }} transition={{
      duration: 0.3
    }} className="tw-overflow-hidden"><div className="tw-max-h-[19.75rem] tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 hover:tw-scrollbar-thumb-iron-300">{voters.length > 0 || isLoading ? <><div className="tw-divide-x-0 tw-divide-y tw-divide-solid tw-divide-iron-800">{voters.map((voter, index) => <SingleWaveDropVoter voter={voter} key={voter.voter.id} position={index + 1} creditType={drop.wave.voting_credit_type} />)}</div>{(isFetchingNextPage || isLoading) && <div className="tw-h-0.5 tw-w-full tw-overflow-hidden tw-bg-iron-800"><div className="tw-h-full tw-w-full tw-animate-loading-bar tw-bg-indigo-400" /></div>}<div ref={intersectionElementRef} /></> : <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-space-y-4 tw-py-6 tw-text-iron-400"><div className="tw-group tw-relative"><div className="tw-absolute tw-inset-0 tw-animate-[spin_4s_linear_infinite] tw-rounded-full tw-bg-gradient-to-br tw-from-primary-400/20 tw-via-indigo-500/10 tw-to-iron-800/10 group-hover:tw-from-primary-400/30" /><div className="tw-absolute tw-inset-0 tw-animate-[spin_5s_linear_infinite] tw-rounded-full tw-bg-gradient-to-tr tw-from-iron-800/10 tw-via-indigo-500/10 tw-to-primary-400/20 group-hover:tw-to-primary-400/30" /><div className="tw-bg-gradient-radial tw-absolute tw-inset-0 tw-animate-pulse tw-from-primary-300/5 tw-to-transparent" /><svg className="tw-relative tw-size-8 tw-flex-shrink-0 tw-text-white/60" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M22.7 13.5L20.7005 11.5L18.7 13.5M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C15.3019 3 18.1885 4.77814 19.7545 7.42909M12 7V12L15 14" /></svg></div><div className="tw-flex tw-flex-col tw-items-center tw-gap-y-2"><span className="tw-bg-gradient-to-br tw-from-iron-200 tw-via-iron-300 tw-to-iron-400 tw-bg-clip-text tw-text-base tw-font-semibold tw-tracking-tight tw-text-transparent">Be the First to Make a Vote</span><p className="tw-mb-0 tw-max-w-64 tw-text-center tw-text-sm tw-text-iron-500">Vote on this drop to see voter rankings appear here.</p></div></div>}</div></motion.div>;
    $[49] = drop.wave.voting_credit_type;
    $[50] = intersectionElementRef;
    $[51] = isFetchingNextPage;
    $[52] = isLoading;
    $[53] = isVotersOpen;
    $[54] = voters;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== t25) {
    t26 = <AnimatePresence>{t25}</AnimatePresence>;
    $[56] = t25;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== t24 || $[59] !== t26) {
    t27 = <div>{t24}{t26}</div>;
    $[58] = t24;
    $[59] = t26;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  return t27;
};
function _temp() {
  const headers = {
    Accept: "text/csv"
  };
  const apiAuth = getStagingAuth();
  const walletAuth = getAuthJwt();
  if (apiAuth) {
    headers["x-6529-auth"] = apiAuth;
  }
  if (walletAuth) {
    headers.Authorization = `Bearer ${walletAuth}`;
  }
  return headers;
}
function _temp2(current) {
  return !current;
}